import { GetServerSidePropsContext } from "next";
import TrunkLink from "../../../components/TrunkLink";
import { definitions } from "../../../types/supabase";
import { addCachingHeaders } from "../../../utils/addCachingHeaders";
import { getOptionsForPageRendering } from "../../../utils/cms/getOptionsForPageRendering";
import {
  ImageRender,
  TextRender,
} from "../../../utils/cms/renderableResources";
import xmsSupabase from "../../../utils/xmsSupabase";
import Page, { PageProps } from "../../cms/page/[pageId]";
import { NoCohortIdentifier } from "@/utils/featureFlags";

type PageConfigurationViewData = definitions["resources"] & {
  pages_component_resources_id: string;
  component_property: string;
  page_id: string;
};
type PageSnippet = {
  id: string; // page id
  imageResource?: definitions["resources"] | null;
  title?: definitions["resources"] | null;
  description?: definitions["resources"] | null;
  slug?: string;
};

const BlogPostEntryCard = ({
  brandAbbreviation,
  page,
}: {
  brandAbbreviation: string;
  page: PageSnippet;
}) => {
  return (
    <TrunkLink
      brandAbbreviation={brandAbbreviation}
      href={`/blog/${page.slug}`}
    >
      <div className="flex flex-col lg:flex-row bg-slate-50 p-4 items-center mb-4">
        <div className="w-32 mx-4">
          <ImageRender resource={page.imageResource} />
        </div>

        <div className="flex flex-col max-w-prose">
          <h2 className="text-xl">
            <TextRender resource={page.title} />
          </h2>
          <p>
            <TextRender resource={page.description} className="line-clamp-4" />
          </p>
        </div>
      </div>
    </TrunkLink>
  );
};

const Blog = ({
  brandAbbreviation,
  pages,
  ...rest
}: { brandAbbreviation: string; pages: Array<PageSnippet> } & PageProps) => {
  return (
    <Page {...rest} childrenClassName={`flex-1`}>
      <div className="flex flex-col gap-y-4 my-2 p-8">
        <div className="m-auto justify-center">
          <h2 className="text-2xl bold my-4">Recent Blog Posts</h2>
          {pages &&
            pages.map((page) => {
              return (
                <BlogPostEntryCard
                  brandAbbreviation={brandAbbreviation}
                  page={page}
                  key={page.id}
                />
              );
            })}
        </div>
      </div>
    </Page>
  );
};

export default Blog;

export const getServerSideProps = addCachingHeaders(
  async (context: GetServerSidePropsContext) => {
    const { brandAbbreviation } = context.params || {};

    const brand = xmsSupabase.ensureOne<definitions["brands"]>(
      await xmsSupabase.supabase
        .from("brands")
        .select("*")
        .eq("abbreviation", brandAbbreviation)
    );
    const marketingTrunkPage = xmsSupabase.ensureOne<{ page_id: string }>(
      await xmsSupabase.supabase
        .from("marketing_trunk_pages")
        .select("*")
        .eq("brand_id", brand.id)
        .eq("path", "blogs")
    );

    const opts = await getOptionsForPageRendering(
      marketingTrunkPage.page_id,
      NoCohortIdentifier
    );

    const blogPages = xmsSupabase.getData<definitions["pages"]>(
      await xmsSupabase.supabase
        .from("blog_posts")
        .select("*")
        .eq("brand_id", brand.id)
        .eq("status", "published")
    );

    const data = xmsSupabase.getData<PageConfigurationViewData>(
      await xmsSupabase.supabase.from("page_configurations").select("*")
    );

    const pageData: Array<PageSnippet> = [];
    for (const blogPage of blogPages) {
      const pageResources = data.filter((d) => d.page_id === blogPage.id);

      const imageResource = pageResources.find(
        (r) => r.component_property === "primaryDisplayImage"
      );
      const title = pageResources.find((r) => r.component_property === "title");
      const description = pageResources.find(
        (r) => r.component_property === "metaDescription"
      );

      pageData.push({
        slug: blogPage.slug,
        id: blogPage.id,
        imageResource: imageResource ?? null,
        title: title ?? null,
        description: description ?? null,
      });
    }

    return {
      props: {
        pages: pageData,
        brandAbbreviation,
        ...opts,
      },
    };
  }
);
